
import { LightbulbIcon, ShieldCheck, TrendingUp, Users, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useState } from "react";

const ConsultingBenefits = () => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  
  const handleTooltipToggle = () => {
    setIsTooltipOpen(prev => !prev);
  };

  return (
    <section className="section-spacing bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block text-primary font-medium mb-4 px-3 py-1 bg-primary/10 rounded-full text-sm animate-fade-in">
            Der Mehrwert
          </span>
          <h2 className="animate-fade-in mb-6" style={{ animationDelay: '100ms' }}>
            Wie Katalysator-Führung wirkt
          </h2>
          <p className="text-joerg-600 text-lg leading-relaxed animate-fade-in" style={{ animationDelay: '200ms' }}>
            Unternehmen, die auf eine menschenzentrierte Führungskultur setzen, erleben nachhaltige Veränderungen – in der Zusammenarbeit, in der Bindung und in den Ergebnissen.
          </p>
        </div>

        {/* Featured benefit with Von Restorff effect - stands out visually */}
        <div className="mb-12 max-w-4xl mx-auto">
          <div className="bg-primary/5 p-8 rounded-2xl border-l-4 border-primary shadow-soft">
            <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
              <div className="flex-shrink-0 bg-white p-3 rounded-full shadow-md">
                <TrendingUp className="h-10 w-10 text-primary" />
              </div>
              <div className="flex-grow text-left">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-2xl font-semibold text-joerg-800">Bis zu 33% höhere Profitabilität</h3>
                  <TooltipProvider>
                    <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
                      <TooltipTrigger asChild>
                        <button 
                          onClick={handleTooltipToggle}
                          className="text-primary hover:text-primary-dark p-1 touch-target"
                          aria-label="Quelle anzeigen"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div className="text-xs leading-normal">
                          <p className="font-medium mb-1">Quellen:</p>
                          <p>Gallup-Studie "State of the Global Workplace" (2022)</p>
                          <p>Harvard Business Review: "The Business Case for Purpose" (2021)</p>
                          <p>McKinsey Report: "Organizational Health Index" (2023)</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-joerg-700">
                  Studien belegen: Unternehmen mit entwickelter emotionaler Intelligenz in der Führung erzielen durchschnittlich 
                  um ein Drittel bessere finanzielle Ergebnisse und weisen eine deutlich höhere Mitarbeiterbindung auf.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Other benefits in a clean, consistent grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-joerg-50 p-8 rounded-2xl shadow-soft hover:shadow-elevation transition-all duration-300 animate-fade-in" style={{ animationDelay: '0ms' }}>
            <div className="flex flex-col items-center">
              <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center text-primary shadow-sm mb-2">
                <Users className="h-5 w-5" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-center">Höhere Mitarbeiterbindung</h3>
              <p className="text-joerg-600 text-left">
                Menschen bleiben, wo sie gesehen und wertgeschätzt werden. Katalysator-Führung reduziert Fluktuation um bis zu 40%.
              </p>
            </div>
          </div>
          
          <div className="bg-joerg-50 p-8 rounded-2xl shadow-soft hover:shadow-elevation transition-all duration-300 animate-fade-in" style={{ animationDelay: '150ms' }}>
            <div className="flex flex-col items-center">
              <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center text-primary shadow-sm mb-2">
                <LightbulbIcon className="h-5 w-5" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-center">Gesteigerte Innovation</h3>
              <p className="text-joerg-600 text-left">
                In einer Kultur des Vertrauens werden Ideen frei geteilt und Wandel aktiv gestaltet statt nur reaktiv bewältigt.
              </p>
            </div>
          </div>
          
          <div className="bg-joerg-50 p-8 rounded-2xl shadow-soft hover:shadow-elevation transition-all duration-300 animate-fade-in" style={{ animationDelay: '300ms' }}>
            <div className="flex flex-col items-center">
              <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center text-primary shadow-sm mb-2">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-center">Nachhaltige Resilienz</h3>
              <p className="text-joerg-600 text-left">
                Unternehmen mit starker Führungskultur sind 4x widerstandsfähiger in Krisen und erholen sich schneller von Marktturbulenzen.
              </p>
            </div>
          </div>
        </div>
        
        {/* Simplified explanation box */}
        <div className="mt-12 p-6 bg-joerg-50 rounded-xl border border-joerg-100 max-w-3xl mx-auto">
          <h3 className="text-lg font-medium mb-3 text-joerg-800">Katalysator-Führung: Das Prinzip</h3>
          <p className="text-joerg-600 mb-0">
            Wie ein Katalysator in der Chemie Reaktionen beschleunigt ohne selbst verbraucht zu werden, 
            schafft dieser Führungsstil optimale Bedingungen für Mitarbeiter, eigenständig Höchstleistungen zu erbringen – 
            mit messbaren wirtschaftlichen Resultaten.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ConsultingBenefits;
